import React from "react";
import { Link } from "react-router-dom";
import "./CourseElement.css";
import "./WaitingListBox.css";
import { MainContext } from "../contexts/MainContext";
import { apiRequest } from "../functions/requestMulti";
import BookButton from "./BookButton";
import InfoContainer from "./InfoContainer";
import ErrorMessage from "./ErrorMessage";

type Props = {
  clubId: string;
  branchId: string;
  courseId: string;
};

export default class WaitingListBox extends React.Component<
  Props,
  {
    checked: boolean;
    onList: boolean;
    loading: boolean;
    errorMsg: string | null;
  }
> {
  static contextType = MainContext;
  mounted = true;

  constructor(props: Props) {
    super(props);
    this.state = {
      checked: false,
      onList: false,
      loading: false,
      errorMsg: null,
    };
  }

  componentDidMount = () => {
    // Guests can never be on the list, so no request at all.
    if (!this.context.user) {
      this.setState({ checked: true });
      return;
    }
    this.requestEntry();
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  requestEntry = async () => {
    await apiRequest("api/waitingList/my", "GET", {
      targetUserId: this.context.selectedUserId,
      clubId: this.props.clubId,
      branchId: this.props.branchId,
      courseId: this.props.courseId,
    })
      .then(this.handleEntry)
      .catch(this.handleCheckError);
  };

  handleEntry = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({
      checked: true,
      onList: !!(data.success && data.data?.onWaitingList),
    });
  };

  handleCheckError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    // Fail open: showing the button is better than hiding the only CTA, and a
    // duplicate entry is rejected server side. `checked` has to be set here or
    // the button keeps spinning forever.
    this.setState({ checked: true, onList: false });
  };

  joinWaitingList = async () => {
    if (this.state.loading || !this.state.checked || this.state.onList) return;
    this.setState({ loading: true, errorMsg: null });
    // tries = 1: apiRequest retries three times by default, and a retried POST
    // after a partial write would try to create a second entry.
    await apiRequest(
      "api/waitingList/join",
      "POST",
      {
        targetUserId: this.context.selectedUserId,
        clubId: this.props.clubId,
        branchId: this.props.branchId,
        courseId: this.props.courseId,
      },
      1
    )
      .then(this.handleJoin)
      .catch(this.handleRequestError);
  };

  handleJoin = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ loading: false });
    if (data.success) {
      this.setState({ onList: true, errorMsg: null });
      this.context.createInfo(
        "Du stehst jetzt auf der Warteliste.",
        "success",
        4
      );
    } else {
      this.setState({ errorMsg: data.errorMsgDe });
    }
  };

  leaveWaitingList = async () => {
    if (this.state.loading) return;
    this.setState({ loading: true, errorMsg: null });
    await apiRequest(
      "api/waitingList/my",
      "DELETE",
      {
        targetUserId: this.context.selectedUserId,
        clubId: this.props.clubId,
        branchId: this.props.branchId,
        courseId: this.props.courseId,
      },
      1
    )
      .then(this.handleLeave)
      .catch(this.handleRequestError);
  };

  handleLeave = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ loading: false });
    if (data.success) {
      this.setState({ onList: false, errorMsg: null });
      this.context.createInfo(
        "Du wurdest von der Warteliste entfernt.",
        "success",
        4
      );
    } else {
      this.setState({ errorMsg: data.errorMsgDe });
    }
  };

  handleRequestError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    // apiRequest throws away the response body on a non-2xx, so there is no
    // errorMsgDe to show here - only a generic fallback.
    this.setState({
      loading: false,
      errorMsg: "Ein unerwarteter Fehler ist aufgetreten.",
    });
  };

  render() {
    if (!this.context.user) {
      return (
        <>
          <div className="medium-space"></div>
          <InfoContainer>
            Dieses Angebot ist ausgebucht. Melde dich an, um dich auf die
            Warteliste zu setzen.{" "}
            <Link
              to={`/login/${this.props.clubId}?redirect=${window.location.href}`}
            >
              Jetzt anmelden
            </Link>{" "}
            oder{" "}
            <Link
              to={`/sign-up/${this.props.clubId}?redirect=${window.location.href}`}
            >
              registrieren
            </Link>
            .
          </InfoContainer>
        </>
      );
    }

    if (this.state.onList) {
      return (
        <>
          <div className="medium-space"></div>
          <InfoContainer status="success">
            Du stehst auf der Warteliste. Wir melden uns bei dir, sobald ein
            Platz frei wird.
          </InfoContainer>
          <ErrorMessage message={this.state.errorMsg} />
          <div
            className="waiting-list-leave-container"
            onClick={this.leaveWaitingList}
          >
            {this.state.loading
              ? "Wird entfernt …"
              : "Von der Warteliste entfernen"}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="medium-space"></div>
        <InfoContainer>
          Dieses Angebot ist ausgebucht. Setze dich auf die Warteliste – wir
          melden uns bei dir, sobald ein Platz frei wird.
        </InfoContainer>
        <ErrorMessage message={this.state.errorMsg} />
        <BookButton
          loading={this.state.loading || !this.state.checked}
          onClick={this.joinWaitingList}
          title={
            this.state.checked
              ? "Auf die Warteliste setzen"
              : "Warteliste wird geprüft …"
          }
          className={`course-book-btn${
            !this.state.checked ? " course-book-btn-gray" : ""
          }`}
        />
      </>
    );
  }
}
